import React, { useState, useEffect } from 'react';
import { KeyboardDatePicker } from '@material-ui/pickers';
import ErrorMessages from 'components/Global/ErrorMessages';
import mapValidators from 'utility/mapValidators';
import { Grid } from '@material-ui/core';
import usePrevious from 'hooks/usePrevious';
import { addRemoveRequest, deleteRemoveRequest, getTimeWithTimeZone } from 'utility/general';
import moment from 'moment';

const CustomDatePicker = (props) => {
    const {
        name,
        value,
        handleChangeDate = () => null,
        disableOnPermission = false,
        style = {},
        format,
        inputVariant,
        size,
        label,
        disablePast = false,
        isValidValue = () => null,
        forceErrorDisplay,
        allowAction = true,
        validators,
        disabled,
        localTime = false,
        updateApplicationFormOnDisableChange = -1,
        updateSiteFormOnDisableChange = -1,
        onAppFieldValidate,
        hiddenValidationTips = false,
        onChange = () => {},
        customValidationMessage = '',
        test,
        ...rest
    } = props;

    const [errorMessage, setErrorMessage] = useState([]);
    const [isOnBlurTriggered, setOnBlurTrigger] = useState(false);
    const [dateErrorMsg, setDateErrorMsg] = useState();
    const previousDisable = usePrevious(`${allowAction}`);

    const preValidators = usePrevious(validators);
    useEffect(() => {
        if (validators?.toString() !== preValidators?.toString()) {
            onValidation(value);
        }
        // eslint-disable-next-line
    }, [validators]);

    useEffect(() => {
        // updateApplicationFormOnDisableChange = application id
        if (previousDisable && (updateApplicationFormOnDisableChange >= 0 || updateSiteFormOnDisableChange >= 0)) {
            if (!name) {
                throw new Error('name is required');
            }
            const isDisabled = getDisableExceptionPermission();
            if (isDisabled) {
                addRemoveRequest({
                    applicationFormId: updateApplicationFormOnDisableChange,
                    name: name,
                    siteInfoId: updateSiteFormOnDisableChange,
                });
            } else {
                deleteRemoveRequest({
                    applicationFormId: updateApplicationFormOnDisableChange,
                    keys: [name],
                    siteInfoId: updateSiteFormOnDisableChange,
                });
            }
        }

        checkAppFieldValidate();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowAction, disabled]);

    const checkAppFieldValidate = () => {
        if (onAppFieldValidate !== undefined) {
            const isDisabled = getDisableExceptionPermission();

            const isValid = onValidation(value);

            if (isDisabled) {
                onAppFieldValidate(name, isValid);
            } else {
                if (value) {
                    onAppFieldValidate(name, isValid);
                } else {
                    onAppFieldValidate(name, false);
                }
            }
        }
    };

    const getDisableExceptionPermission = () => {
        if (disabled) {
            return true;
        }

        if (allowAction === false) {
            return true;
        }

        return false;
    };

    const getDisableStatus = () => {
        if (disableOnPermission) {
            return true;
        }

        if (disabled) {
            return true;
        }

        if (allowAction === false) {
            return true;
        }

        return false;
    };

    useEffect(() => {
        onValidation(value);
        checkAppFieldValidate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // logic to force error display
    useEffect(() => {
        if (forceErrorDisplay) {
            setOnBlurTrigger(true);
        }
    }, [forceErrorDisplay]);

    const onValidation = (value) => {
        let tempError = mapValidators(value, validators);

        let isValid = true;

        const { minDate, maxDate } = rest;

        if (minDate) {
            if (
                new Date(moment(value).format('YYYY-MM-DD')).getTime() <
                new Date(moment(minDate).format('YYYY-MM-DD')).getTime()
            ) {
                isValid = false;
            }
        }

        if (maxDate) {
            if (
                new Date(moment(value).format('YYYY-MM-DD')).getTime() >
                new Date(moment(maxDate).format('YYYY-MM-DD')).getTime()
            ) {
                isValid = false;
            }
        }

        isValidValue(tempError.length === 0 && isValid);
        setErrorMessage(tempError);

        return tempError.length === 0 && isValid;
    };

    const handleChange = (date) => {
        let result;

        if (localTime) {
            result = new Date(date.setHours(0, 0, 0, 0));
        } else {
            result = new Date(getTimeWithTimeZone(date).format('YYYY-MM-DD'));
        }

        handleChangeDate({
            target: { value: result, name: name },
        });
        onChange(result);
        onValidation(date);
    };

    const handleOnBlur = () => {
        setOnBlurTrigger(true);
    };

    if(!rest.minDate){
        delete rest['minDate']
    }

    return (
        <Grid container className={'customDisabledStyle'}>
            <KeyboardDatePicker
                autoOk={true}
                className="custom-date-picker"
                style={{ margin: 0, ...style }}
                inputVariant={inputVariant ? inputVariant : 'outlined'}
                size={size ? size : 'small'}
                fullWidth
                // disableToolbar
                allowKeyboardControl={false}
                variant="inline"
                format={format ? format : 'yyyy-MM-dd'}
                disablePast={disablePast}
                KeyboardButtonProps={{ 'aria-label': 'change date' }}
                label={label ? label : 'Date'}
                InputProps={{
                    readOnly: true,
                    style: {
                        fontSize: 14,
                    },
                }}
                InputLabelProps={{
                    style: {
                        fontSize: 14,
                    },
                }}
                name={name}
                value={value}
                onChange={(date) => handleChange(date)}
                onBlur={handleOnBlur}
                error={
                    hiddenValidationTips ? false : (errorMessage.length > 0 && isOnBlurTriggered) || dateErrorMsg !== ''
                }
                helperText={!hiddenValidationTips && (errorMessage.length ? '' : dateErrorMsg)}
                onError={(err) => {
                    if (customValidationMessage && err) {
                        return setDateErrorMsg(customValidationMessage);
                    }

                    setDateErrorMsg(err);
                }}
                disabled={getDisableStatus()}
                {...rest}
            />
            <ErrorMessages isOnBlurTriggered={isOnBlurTriggered} errorMessage={errorMessage} />
        </Grid>
    );
};

export default CustomDatePicker;
